/**
 * ZOffsetField Component
 *
 * Fetches and displays the Z offset value from the print-start event.
 * Mimics hardware-digester behavior with React Admin 5.x and Material-UI v5.
 */

import { useState, useEffect } from 'react';
import { InputLabel, LinearProgress } from '@mui/material';
import { useDataProvider, useRecordContext } from 'react-admin';
import { Printing, EventType, Event } from '../../../types';

interface PrintStartEventContent {
  fields?: {
    zOffset?: string;
  };
}

export const ZOffsetField = () => {
  const record = useRecordContext<Printing>();
  const provider = useDataProvider();
  const [zOffset, setZOffset] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchPrintStartEvent = async () => {
      if (!record?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch print-start event type
        const { data: eventTypes } = await provider.getList<EventType>('eventTypes', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'name', order: 'ASC' },
          filter: { name: 'print-start' },
        });

        if (!eventTypes?.[0]?.id) {
          setLoading(false);
          return;
        }

        // Fetch print-start event for this printing
        const { data: events } = await provider.getList<Event>('events', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'eventTime', order: 'ASC' },
          filter: {
            eventTypeId: eventTypes[0].id,
            printingId: record.id,
          },
        });

        if (events?.[0]?.rawContent) {
          try {
            const content: PrintStartEventContent = JSON.parse(events[0].rawContent);
            setZOffset(content?.fields?.zOffset || '');
          } catch {
            // Silently fail if rawContent is not valid JSON
          }
        }
      } catch {
        // Error fetching z-offset
      } finally {
        setLoading(false);
      }
    };

    fetchPrintStartEvent();
  }, [provider, record?.id]);

  return (
    <>
      <InputLabel shrink={true} sx={{ margin: '8px 0 4px 0' }}>
        <span>Z Offset</span>
      </InputLabel>
      {loading ? (
        <div style={{ width: 60 }}>
          <LinearProgress />
        </div>
      ) : (
        <span>{zOffset || 'N/A'}</span>
      )}
    </>
  );
};

ZOffsetField.defaultProps = {
  label: 'Z Offset',
};
